/**
 * Duplicate warning shown during startup intake (F14).
 *
 * PRD F14 asks for duplicate detection on creation — as a warning, not a wall.
 * So this surfaces probable matches with the reason each one fired and a link
 * to open it, and leaves the decision with the person: two records can
 * legitimately share a domain before someone confirms they are the same
 * company. The create button is never disabled by it.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 9.4, PRD F14, §9.7
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { DuplicateMatch } from '@nexus/contracts';

import { Badge } from '../../../components/ui/Badge';
import { Card, CardBody, CardHead } from '../../../components/ui/Card';
import { Kicker } from '../../../components/ui/Kicker';
import { startupPath } from '../../../lib/routes';

export function DuplicateWarning({
  duplicates,
}: {
  duplicates: readonly DuplicateMatch[];
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    /* `role="status"` rather than `alert`: it is advisory and appears as the
       user types, so it should be announced politely, not interrupt them. */
    <div role="status">
      <Card>
        <CardHead>
          <Kicker as="h2">{t('startups.form.duplicatesTitle')}</Kicker>
          <Badge tone="warn">{t('alerts.severity.WARNING')}</Badge>
        </CardHead>
        <CardBody>
          <p className="text-body mb-s3 text-sm font-light">
            {t('startups.form.duplicatesBody')}
          </p>
          <ul className="gap-s2 flex flex-col">
            {duplicates.map((duplicate) => (
              <li
                key={duplicate.id}
                className="border-gray-100 pb-s2 flex items-baseline justify-between gap-3 border-b last:border-b-0 last:pb-0"
              >
                <span className="min-w-0">
                  <span className="text-navy-text text-sm font-medium">
                    {duplicate.name}
                  </span>
                  <span className="text-body block text-[13px] font-light">
                    {duplicate.reasons.join(' · ')}
                  </span>
                </span>
                <Link
                  to={startupPath(duplicate.id)}
                  className="text-blue flex-none text-xs underline"
                >
                  {t('startups.form.viewExisting')}
                </Link>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>
    </div>
  );
}
